export interface AnalyticsPageMetric {
  metric: string; value: number | null; source_metric: string | null;
  unit: "count" | "ratio" | "seconds" | "minutes" | "position" | "currency";
  aggregation: "sum" | "weighted_ratio" | "weighted_average" | "latest" | "provider_defined";
  availability: "available" | "partial" | "unavailable" | "unsupported";
  finality: "provisional" | "final" | "corrected" | null;
  numerator_value: number | null; denominator_value: number | null;
}
export interface AnalyticsDrilldownRow { dimensions: Record<string, string>; period_start: string; period_end: string; metrics: AnalyticsPageMetric[]; }

function metricValue(row: AnalyticsDrilldownRow, metric: string): number | null {
  return row.metrics.find((entry) => entry.metric === metric)?.value ?? null;
}

/** Base64url keeps arbitrary GA4 sessionSource values safe in a single route segment. */
export function encodeSessionSource(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function decodeSessionSource(value: string): string | null {
  try {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((value.length + 3) % 4);
    const binary = atob(base64);
    return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
  } catch { return null; }
}

/** Compare GSC absolute URLs with GA4 path(+query) landing pages. */
export function normalizeAnalyticsPageUrl(value: string, canonicalUrl?: string | null): string {
  const raw = value.trim();
  if (!raw) return "/";
  try {
    const base = canonicalUrl ? new URL(canonicalUrl) : new URL("https://property.invalid");
    const url = new URL(raw, base);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();
    const path = url.pathname.replace(/\/+$/, "") || "/";
    return `${host}${path}`;
  } catch {
    const path = raw.split(/[?#]/, 1)[0]?.replace(/\/+$/, "") || "/";
    return path.startsWith("/") ? path : `/${path}`;
  }
}

export type MergedAnalyticsPage = { key: string; raw: string; gsc?: AnalyticsDrilldownRow; ga4?: AnalyticsDrilldownRow };
export function mergeTopPages(gscRows: AnalyticsDrilldownRow[], ga4Rows: AnalyticsDrilldownRow[], canonicalUrl?: string | null): MergedAnalyticsPage[] {
  const pages = new Map<string, MergedAnalyticsPage>();
  for (const row of gscRows) {
    const raw = row.dimensions.page ?? "—"; const key = normalizeAnalyticsPageUrl(raw, canonicalUrl);
    pages.set(key, { ...(pages.get(key) ?? { key, raw }), raw, gsc: row });
  }
  for (const row of ga4Rows) {
    const raw = row.dimensions.landingPagePlusQueryString ?? "—"; const key = normalizeAnalyticsPageUrl(raw, canonicalUrl);
    pages.set(key, { ...(pages.get(key) ?? { key, raw }), ga4: row });
  }
  const score = (page: MergedAnalyticsPage) => page.gsc
    ? metricValue(page.gsc, "search.clicks") ?? 0
    : metricValue(page.ga4!, "analytics.sessions") ?? 0;
  return [...pages.values()].sort((a, b) => score(b) - score(a));
}
